import {
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  Link,
  Text,
} from "@chakra-ui/react";
import { HiPencil } from "react-icons/hi";
import { useNavigate, useParams } from "react-router-dom";

import { formatPhoneNumber } from "src/common/phone-numbers";
import { PageContent } from "src/layout/PageContent";
import { useDealerOrganizationQueryData } from "src/routes/DealerOrganization/queries";

const formatForHref = (url: string) => {
  // The following regex expression is used to determine if the given website
  // explicitly uses HTTP or HTTPS as its protocol. It will return the
  // website with the appropriate prefix for use as an href value.
  const isHttp = /^https?:\/\/.+$/i.test(url);

  return `${isHttp ? "" : "//"}${url}`;
};

export const DealerOrganizationDetails = () => {
  const { id } = useParams<"id">();
  const navigate = useNavigate();

  const data = useDealerOrganizationQueryData(id);

  if (!data) return <></>;

  return (
    <>
      <Flex justifyContent={"flex-end"} width={"80%"}>
        <HiPencil size="18" />
        <Button
          paddingLeft={2}
          variant="link"
          colorScheme="brand.blue"
          onClick={() => navigate(`/dealers/${id}/edit`)}
        >
          Edit Profile
        </Button>
      </Flex>
      <PageContent>
        <Grid templateColumns={"repeat(2, 1fr)"} gap={4}>
          <GridItem colSpan={2}>
            <Heading size="md">Company Information</Heading>
          </GridItem>

          <GridItem colSpan={2}>
            <Divider />
          </GridItem>

          <GridItem>
            <Text>Company Name</Text>
          </GridItem>
          <GridItem>
            <Text>{data.name}</Text>
          </GridItem>

          <GridItem>
            <Text>Company DBA</Text>
          </GridItem>
          <GridItem>
            <Text>{data.dba || "-"}</Text>
          </GridItem>

          <GridItem>
            <Text>Account ID</Text>
          </GridItem>
          <GridItem>
            <Text>{data.accountId}</Text>
          </GridItem>

          <GridItem>
            <Text>{"Address Line 1"}</Text>
          </GridItem>
          <GridItem>
            <Text>{data.addressLine1}</Text>
          </GridItem>

          {!!data.addressLine2 && (
            <>
              <GridItem>
                <Text>Address Line 2</Text>
              </GridItem>
              <GridItem>
                <Text>{data.addressLine2}</Text>
              </GridItem>
            </>
          )}

          <GridItem>
            <Text>City</Text>
          </GridItem>
          <GridItem>
            <Text>{data.city}</Text>
          </GridItem>

          <GridItem>
            <Text>State</Text>
          </GridItem>
          <GridItem>
            <Text>{data.state}</Text>
          </GridItem>

          <GridItem>
            <Text>Zip Code</Text>
          </GridItem>
          <GridItem>
            <Text>{data.zipCode}</Text>
          </GridItem>

          <GridItem>
            <Text>Company Phone Number</Text>
          </GridItem>
          <GridItem>
            <Text>{formatPhoneNumber(data.phone)}</Text>
          </GridItem>

          <GridItem>
            <Text>Company Website</Text>
          </GridItem>
          <GridItem>
            <Link href={formatForHref(data.website)} isExternal>
              <Text>{data.website}</Text>
            </Link>
          </GridItem>

          <GridItem>
            <Text>Dealer Type</Text>
          </GridItem>
          <GridItem>
            <Text>{data.dealerType}</Text>
          </GridItem>
        </Grid>
      </PageContent>

      {data.pointOfContact && (
        <PageContent>
          <Grid templateColumns={"repeat(2, 1fr)"} gap={4}>
            <GridItem colSpan={2}>
              <Heading size={"md"}>Contact Information</Heading>
            </GridItem>

            <GridItem colSpan={2}>
              <Divider />
            </GridItem>

            <GridItem>
              <Text>First name</Text>
            </GridItem>

            <GridItem>
              <Text>{data.pointOfContact.user.firstName}</Text>
            </GridItem>

            <GridItem>
              <Text>Last name</Text>
            </GridItem>

            <GridItem>
              <Text>{data.pointOfContact.user.lastName}</Text>
            </GridItem>

            <GridItem>
              <Text>Phone number</Text>
            </GridItem>

            <GridItem>
              <Text>{formatPhoneNumber(data.pointOfContact.user.phone)}</Text>
            </GridItem>

            <GridItem>
              <Text>Email</Text>
            </GridItem>

            <GridItem>
              <Link
                href={`mailto:${data.pointOfContact.user.email}`}
                isExternal
              >
                <Text>{data.pointOfContact.user.email}</Text>
              </Link>
            </GridItem>
          </Grid>
        </PageContent>
      )}
    </>
  );
};
